/**
 * New Year doodles — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const newYearDoodlesPack: ToilePack = {
    slug: 'new-year-doodles',
    category: 'season',
    name: 'New Year doodles',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'mixed', label: 'Mixed doodles', subject: 'Small new year doodles packed edge to edge: a clock, a glass, a hat, a horn, a star, a balloon, a firework, no gaps'},
        {key: 'party', label: 'Party doodles', subject: 'Small party doodles packed edge to edge: streamers, confetti, a cake, a disco ball, a bottle, no gaps'},
        {key: 'goals', label: 'Goal doodles', subject: 'Small new year goals doodles packed edge to edge: a checkbox, a trophy, a book, a plant, a running shoe, no gaps'},
    ],
    styles: [
        {key: 'simple', label: 'Simple lines', style: 'bold single-weight black outline drawing, large open shapes, clean closed line art', palette: 'Black ink only', framing: 'Centered on a pure white page with a white margin, the full page used'},
        {key: 'detailed', label: 'Detailed', style: 'intricate fine black line art, dense decorative detail, clean closed line art', palette: 'Black ink only', framing: 'Filling the page edge to edge inside a thin white margin'},
        {key: 'doodle', label: 'Doodle', style: 'hand-drawn marker doodle line art, loose confident strokes, clean closed line art', palette: 'Black ink only', framing: 'Scattered across the page with a white margin'},
    ],
    directions: ['add a resolution list', 'in gold line weight', 'for a bullet journal spread'],
    directionHint: 'try: add a resolution list',
    seo: {
        title: 'New Year doodles: mixed, party, goals',
        metaDescription: 'Nine New Year doodle sheets — mixed, party, resolution goals — packed edge to edge in simple, detailed or doodle line art.',
        h1: 'New Year doodles: mixed, party and resolution-goal sheets',
        tagline: 'Nine doodle sheets, three New Year themes, three line styles each — packed edge to edge, ready for the journal.',
        intro: 'A doodle sheet packed edge to edge for midnight: a clock, a glass, a party hat, a firework, no gaps. These nine run three themes — a mixed page, a party page of streamers and confetti, and a resolution-goals page of checkboxes and trophies — in bold simple lines, intricate detailed line art, or loose doodle strokes. Color the page or trace it into a bullet journal spread.',
        useCases: [
            'a sticker-making craft session',
            'a bullet journal or planner spread',
            "a New Year's Eve party filler activity",
            'a resolution-tracking journal page',
        ],
        faq: [
            {q: 'What size do the sheets print at?', a: 'Each sheet is drawn square with a white margin, so it prints clean on standard paper.'},
            {q: 'Can a sheet be traced into a planner?', a: 'Yes — every doodle is a clean closed outline, easy to trace or crop into a bullet journal spread.'},
            {q: 'Can I get a different New Year theme?', a: "Yes — that's what the direction field is for. Type a different theme, like a resolution list, and the sheet redraws in the same three line styles."},
        ],
    },
}
